// packages/world-model/src/traits/identity/identityTrait.ts

import { type SnippetMap } from '@sharpee/if-domain';
import { ITrait } from '../trait.js';
import { TraitType } from '../trait-types.js';

/**
 * Identity trait provides basic naming and description for entities.
 * This is one of the most fundamental traits in IF.
 * 
 * This is a pure data structure - all validation and logic
 * should be handled by IdentityBehavior.
 */
export class IdentityTrait implements ITrait {
  static readonly type = TraitType.IDENTITY;
  readonly type = TraitType.IDENTITY;
  
  /** Primary name of the entity */
  name = '';
  
  /** Full description shown when examining */
  description = '';

  /**
   * Message ID for localized name (ADR-107).
   * If set, the language layer resolves this ID to the actual name text.
   * Takes precedence over literal `name` if both are set.
   */
  nameId?: string;

  /**
   * Message ID for localized description (ADR-107).
   * If set, the language layer resolves this ID to the actual description text.
   * Takes precedence over literal `description` if both are set.
   */
  descriptionId?: string;

  /**
   * Marker→snippet table for this entity's description (ADR-209 machinery;
   * GH #364). When present, `{snippet:name}` markers in the description are
   * spliced at examine time exactly as `RoomTrait.snippets` markers are in
   * a room's prose: a fixed text, a variant list with a selector, or a
   * gated entry. Absent means no splice pass — the description renders as
   * it is. The Chord loader compiles an entity's `{marker}` phrases here.
   */
  snippets?: SnippetMap;

  /** Alternative names/aliases the entity can be referred to by */
  aliases: string[] = [];
  
  /** Brief description shown in room listings */
  brief?: string;
  
  /** Whether the entity has a proper name (like "John" vs "a man") */
  properName = false;
  
  /** Article to use with the name ("a", "an", "the", "some", or empty for proper names) */
  article = 'a';

  /**
   * Pronoun set for gendered / neopronoun reference (ADR-242 D6): one of
   * the standard sets ('he' | 'she' | 'it' | 'they') or a named set
   * registered with the language provider. Absent = the phrase chain's
   * by-number fallback — no default is injected anywhere.
   */
  pronounSet?: string;

  /**
   * Author-supplied plural form for irregular nouns (ADR-190), e.g. "geese" for
   * "goose". The `list`/`count` formatters use this when set, else fall back to the
   * `pluralize()` heuristic. Flows to the language layer via `entityInfoFrom`.
   */
  plural?: string;
  
  /** Whether this entity is concealed from normal view */
  concealed = false;

  /**
   * Whether this holder's contents are described in its own prose and
   * never listed by the room ("On the rope wares you see a length of
   * rope." is not printed). The contents stay in scope — examinable,
   * takeable — only the listing is withheld. Chord's `unlisted` adjective
   * sets it (2026-09-06, the Secret Letter stall displays: the 2009 game
   * never listed a display's wares; the display's prose IS the listing).
   */
  contentsUnlisted = false;
  
  /** Weight of the object (undefined = negligible/not tracked) */
  weight?: number;
  
  /** Volume of the object (undefined = negligible/not tracked) */
  volume?: number;
  
  /** Size category of the object */
  size?: 'tiny' | 'small' | 'medium' | 'large' | 'huge';

  /**
   * Grammatical number for inanimate objects (ADR-089).
   * - 'singular': "it" (default) - "take it", "the lamp"
   * - 'plural': "them" - "take them", "the coins"
   * Only used for entities WITHOUT ActorTrait.
   */
  grammaticalNumber?: 'singular' | 'plural';

  /**
   * Adjectives that can be used to refer to this entity (ADR-093).
   * Used for disambiguation when multiple entities share a noun.
   * Example: ['yellow'] for "yellow button" vs ['red'] for "red button"
   */
  adjectives: string[] = [];

  /**
   * Noun type for article/formatter selection (ADR-095).
   * - 'common': Regular countable noun (a sword, the sword)
   * - 'proper': Proper name (John, not "a John")
   * - 'mass': Uncountable noun (water, not "a water" - use "some water")
   * - 'unique': One-of-a-kind (the sun, not "a sun")
   * - 'plural': Inherently plural (scissors, pants)
   *
   * If not set, formatters use `properName` and `grammaticalNumber` as fallback.
   */
  nounType?: 'common' | 'proper' | 'mass' | 'unique' | 'plural';

  /**
   * Points awarded when player first takes this item (ADR-129).
   * Stdlib taking action calls world.awardScore() with this value.
   * Deduplication is automatic via entity ID.
   */
  points?: number;

  /**
   * Description for the score ledger entry (ADR-129).
   * Defaults to the entity name if not set.
   */
  pointsDescription?: string;

  constructor(data?: Partial<IdentityTrait>) {
    if (data) {
      Object.assign(this, data);
    }
  }
}
